import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bell,
  Check,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Info,
  Undo2,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  type ActionRunner,
  type ActionState,
  BELL_SHOWS,
  type Level,
  type Note,
  type NoteLinkData,
  useNotifications,
} from "../notify";

/**
 * The bell in the header, and everything that comes out of it.
 *
 * A notification arrives as a bubble under the bell, stays its eight seconds, then goes into the bell
 * and counts as unread. Opening the bell reads all of them at once: the count is a "there is
 * something you have not seen", not a per-item chore.
 *
 A notification about something opens it when tapped: "Draft kept" opens the draft, "Received 9
 * lots" the invoice (Tyler, 2026-10-01; until then clicking one did nothing, 2026-09-21). One can also
 * carry an action (undo, so far) as a button of its own, which goes up into the bell with it and stays
 * pressable there (Tyler, 2026-09-23).
 *
 * The bell shows the last ten. Its title is a link to the Notifications page, which has the rest of the
 * thirty days they are kept for, actions and all (Tyler, 2026-09-24).
 */

const ICONS: Record<Level, typeof Info> = {
  info: Info,
  notice: CircleCheck,
  warning: CircleAlert,
};

const LABELS: Record<Level, string> = {
  info: "Information",
  notice: "Worth noticing",
  warning: "Warning",
};

export function NotificationBell() {
  const { notes, unread, markAllRead, dismiss, act, runners } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const showing = notes.filter((n) => n.showing);

  // Clicking away closes the list, as the menus do.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Element)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => {
    setOpen((was) => {
      if (!was) markAllRead();
      return !was;
    });
  };

  return (
    <div className="bell-wrap" ref={wrap}>
      <button
        type="button"
        className="icon-button bell"
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        title="Notifications"
        onClick={toggle}
      >
        <Bell size={18} />
        {unread > 0 ? (
          <span className="bell-count" aria-hidden>
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </button>

      {/* Live region: the bubbles are the announcement, so a screen reader hears them once. */}
      <div className="bubbles" role="status" aria-live="polite">
        {/* Not while the list is open: the list already has it at the top, and the bubble only
            peeked out from behind the panel (an undo pressed in the bell says so this way). */}
        {(open ? [] : showing).map((note) => (
          <Bubble
            key={note.id}
            note={note}
            runner={note.action ? runners[note.action.data.kind] : undefined}
            onDismiss={() => dismiss(note.id)}
            onAct={() => act(note.id)}
          />
        ))}
      </div>

      {open ? (
        <div className="bell-panel">
          <div className="bell-panel-head">
            <Link to="/notifications" className="bell-panel-link" onClick={() => setOpen(false)}>
              <strong>Notifications</strong>
              <ArrowRight size={14} aria-hidden />
              <span className="sr-only">, see all</span>
            </Link>
            {notes.length > BELL_SHOWS ? (
              <span className="muted">
                {BELL_SHOWS} of {notes.length}
              </span>
            ) : notes.length > 0 ? (
              <span className="muted">{notes.length}</span>
            ) : null}
          </div>
          {notes.length === 0 ? (
            <p className="muted bell-empty">Nothing yet. What the app has to say turns up here.</p>
          ) : (
            <div className="bell-list">
              {notes.slice(0, BELL_SHOWS).map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  runner={note.action ? runners[note.action.data.kind] : undefined}
                  onAct={() => act(note.id)}
                  onOpen={() => setOpen(false)}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** One notification as a row: in the bell, and on the Notifications page. */
export function NoteRow({
  note,
  runner,
  onAct,
  onOpen,
  compact = true,
}: {
  note: Note;
  runner: ActionRunner | undefined;
  onAct: () => void;
  /** Its link was followed: the bell closes behind it. */
  onOpen?: () => void;
  compact?: boolean;
}) {
  return (
    <div className={`bell-item level-${note.level}${note.action ? " has-action" : ""}`}>
      <NoteIcon level={note.level} />
      <NoteText note={note} onOpen={onOpen}>
        <div className="muted bell-when">{when(note.at)}</div>
      </NoteText>
      {note.action && runner ? (
        <ActionButton state={note.action.state} runner={runner} compact={compact} onAct={onAct} />
      ) : null}
    </div>
  );
}

/**
 * What a notification says. Where it is about something, the words are the way there, and an arrow
 * says so; the action button stays outside, so pressing undo never also opens the invoice.
 */
function NoteText({
  note,
  className,
  onOpen,
  children,
}: {
  note: Note;
  className?: string;
  onOpen?: () => void;
  children?: ReactNode;
}) {
  const words = (
    <>
      <div className="bell-item-title">
        {note.title}
        {note.link ? <ChevronRight className="note-open-arrow" size={14} aria-hidden /> : null}
      </div>
      {note.body ? <div className="muted">{note.body}</div> : null}
      {children}
    </>
  );
  const classes = ["note-text", className].filter(Boolean).join(" ");
  if (!note.link) return <div className={classes}>{words}</div>;
  return (
    <NoteLink link={note.link} className={`${classes} note-open`} onClick={onOpen}>
      {words}
    </NoteLink>
  );
}

/** Where each kind of link goes. A draft of a saved recipe opens on that recipe's edit page. */
function NoteLink({
  link,
  className,
  onClick,
  children,
}: {
  link: NoteLinkData;
  className: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  const props = { className, onClick, children };
  switch (link.kind) {
    case "invoice":
      return <Link to="/invoices/$invoiceId" params={{ invoiceId: link.invoiceId }} {...props} />;
    case "item":
      return <Link to="/items/$itemId" params={{ itemId: link.itemId }} {...props} />;
    case "recipe":
      return <Link to="/recipes/$recipeId" params={{ recipeId: link.recipeId }} {...props} />;
    case "recipe_draft":
      return link.recipeId ? (
        <Link to="/recipes/$recipeId/edit" params={{ recipeId: link.recipeId }} {...props} />
      ) : (
        <Link to="/recipes/builder" search={{ draft: link.draftId }} {...props} />
      );
    case "brew":
      return <Link to="/brews/$brewId" params={{ brewId: link.brewId }} {...props} />;
    case "stock_import":
      return (
        <Link to="/settings/ollie/$importId" params={{ importId: link.importId }} {...props} />
      );
    case "history":
      return <Link to="/movements" {...props} />;
  }
}

function NoteIcon({ level }: { level: Level }) {
  const Icon = ICONS[level];
  return (
    <span className={`note-icon level-${level}`} title={LABELS[level]}>
      <Icon size={16} aria-hidden />
      <span className="sr-only">{LABELS[level]}</span>
    </span>
  );
}

function Bubble({
  note,
  runner,
  onDismiss,
  onAct,
}: {
  note: Note;
  runner: ActionRunner | undefined;
  onDismiss: () => void;
  onAct: () => void;
}) {
  return (
    <div className={`bubble level-${note.level}${note.action ? " has-action" : ""}`}>
      <NoteIcon level={note.level} />
      <NoteText note={note} className="bubble-text" onOpen={onDismiss} />
      {note.action && runner ? (
        <ActionButton state={note.action.state} runner={runner} onAct={onAct} />
      ) : null}
      <button
        type="button"
        className="icon-button bubble-close"
        aria-label="Dismiss"
        onClick={onDismiss}
      >
        <X size={14} />
      </button>
    </div>
  );
}

/**
 * The action a notification carries. In the bubble it says what it does; in the bell, where every
 * row has one line of width to spare, it is the arrow alone, with the word as its name. Once used it
 * says so, rather than disappearing and leaving the row looking as though it never had one.
 */
function ActionButton({
  state,
  runner,
  compact = false,
  onAct,
}: {
  state: ActionState;
  runner: ActionRunner;
  compact?: boolean;
  onAct: () => void;
}) {
  if (state === "done") {
    return (
      <span className="note-action-done muted">
        <Check size={14} aria-hidden /> {runner.done}
      </span>
    );
  }
  const running = state === "running";
  return (
    <button
      type="button"
      className={compact ? "icon-button note-action" : "button small note-action"}
      aria-label={compact ? runner.label : undefined}
      title={compact ? runner.label : undefined}
      aria-busy={running}
      disabled={running}
      onClick={onAct}
    >
      <Undo2 size={14} aria-hidden />
      {compact ? null : running ? runner.running : runner.label}
    </button>
  );
}

export function when(at: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
